import { useMemo } from "react";
import { Receipt } from "lucide-react";
import type { PricingRule } from "@/types/billing";
import type { ServiceTier } from "@/types";

interface PricingSummaryPanelProps {
  pricingRule: PricingRule | null;
  cores: number;
  ramMb: number;
  diskGb: number;
  tier: ServiceTier;
  networkSpeed?: "100m" | "500m" | "1g";
  backupPlan: "none" | "snapshot" | "full" | "incremental";
  /** Show old cost comparison (for resize) */
  oldMonthlyCost?: number;
}

export function PricingSummaryPanel({
  pricingRule,
  cores,
  ramMb,
  diskGb,
  tier,
  networkSpeed = "100m",
  backupPlan,
  oldMonthlyCost,
}: PricingSummaryPanelProps) {
  const estimate = useMemo(() => {
    if (!pricingRule) return null;

    const ramGb = ramMb / 1024;
    const baseCpu = pricingRule.price_per_vcpu * cores;
    const baseRam = pricingRule.price_per_gb_ram * ramGb;
    const baseDisk = pricingRule.price_per_gb_disk * diskGb;

    const multiplierMap: Record<string, number> = {
      A: pricingRule.tier_a_multiplier,
      B: pricingRule.tier_b_multiplier,
      C: pricingRule.tier_c_multiplier,
    };
    const multiplier = multiplierMap[tier] ?? 1;
    const subtotal = (baseCpu + baseRam + baseDisk) * multiplier;

    const networkCostMap: Record<string, number> = {
      "100m": pricingRule.price_network_100m ?? 0,
      "500m": pricingRule.price_network_500m ?? 0,
      "1g": pricingRule.price_network_1g ?? 0,
    };
    const networkCost = networkCostMap[networkSpeed] ?? 0;

    const backupCostMap: Record<string, number> = {
      none: 0,
      snapshot: pricingRule.price_backup_snapshot,
      full: pricingRule.price_backup_full,
      incremental: pricingRule.price_backup_incremental,
    };
    const backupCost = backupCostMap[backupPlan] ?? 0;

    const total = subtotal + networkCost + backupCost;

    return { baseCpu, baseRam, baseDisk, multiplier, subtotal, networkCost, backupCost, total };
  }, [pricingRule, cores, ramMb, diskGb, tier, networkSpeed, backupPlan]);

  if (!pricingRule || !estimate) {
    return (
      <div className="rounded-xl border border-gray-800 bg-gray-900/50 p-6">
        <p className="text-sm text-gray-500">
          Pricing not configured. Contact your administrator.
        </p>
      </div>
    );
  }

  const fmt = (n: number) =>
    n.toLocaleString("fr-FR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  const delta =
    oldMonthlyCost != null ? estimate.total - oldMonthlyCost : null;

  return (
    <div className="rounded-xl border border-gray-800 bg-gray-900/50 p-6 space-y-4">
      <div className="flex items-center gap-2">
        <Receipt className="h-5 w-5 text-blue-400" />
        <h3 className="text-base font-semibold text-gray-200">
          Monthly Estimate
        </h3>
      </div>

      <div className="space-y-2 text-sm">
        {/* CPU */}
        <div className="flex justify-between text-gray-400">
          <span>
            CPU ({cores} vCPU &times; {fmt(pricingRule.price_per_vcpu)})
          </span>
          <span className="text-gray-300">{fmt(estimate.baseCpu)}</span>
        </div>

        {/* RAM */}
        <div className="flex justify-between text-gray-400">
          <span>
            RAM ({fmt(ramMb / 1024)} GB &times; {fmt(pricingRule.price_per_gb_ram)})
          </span>
          <span className="text-gray-300">{fmt(estimate.baseRam)}</span>
        </div>

        {/* Disk */}
        <div className="flex justify-between text-gray-400">
          <span>
            Disk ({diskGb} GB &times; {fmt(pricingRule.price_per_gb_disk)})
          </span>
          <span className="text-gray-300">{fmt(estimate.baseDisk)}</span>
        </div>

        {/* Tier multiplier */}
        <div className="flex justify-between text-gray-400">
          <span>Topology (&times;{estimate.multiplier.toFixed(2)})</span>
          <span className="text-gray-300">{fmt(estimate.subtotal)}</span>
        </div>

        {/* Network */}
        {estimate.networkCost > 0 && (
          <div className="flex justify-between text-gray-400">
            <span>Network ({networkSpeed})</span>
            <span className="text-gray-300">{fmt(estimate.networkCost)}</span>
          </div>
        )}

        {/* Backup */}
        {backupPlan !== "none" && (
          <div className="flex justify-between text-gray-400">
            <span>Backup ({backupPlan})</span>
            <span className="text-gray-300">{fmt(estimate.backupCost)}</span>
          </div>
        )}
      </div>

      {/* Divider */}
      <div className="border-t border-gray-700" />

      {/* Total */}
      <div className="flex justify-between">
        <span className="text-base font-bold text-gray-100">Total / month</span>
        <span className="text-base font-bold text-blue-400">
          {fmt(estimate.total)} EUR
        </span>
      </div>

      {/* Delta (resize mode) */}
      {delta != null && (
        <div className="flex justify-between text-sm">
          <span className="text-gray-400">
            {delta >= 0 ? "Increase" : "Decrease"}
          </span>
          <span
            className={delta >= 0 ? "text-amber-400" : "text-green-400"}
          >
            {delta >= 0 ? "+" : ""}
            {fmt(delta)} EUR/month
          </span>
        </div>
      )}
    </div>
  );
}
